import PropTypes from 'prop-types';
const columnPropTypeShape = {
  /**
   * Overrides the default cell renderer for the column. The function receives
   * the row data and should return a React node to render.
   * (rowData) => React.ReactNode
   */
  cell: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The data key for the column. This is used to get the cell value from the row data.
   */
  dataKey: PropTypes.string.isRequired,
  /**
   * A function that returns a string of class names to apply to the cell.
   * It receives the row data as the its first argument.
   * (rowData) => string
   */
  getCellClass: PropTypes.func,
  /**
   * The displayed label for the column.
   */
  header: PropTypes.node,
  /**
   * The minimum width of the column set as an inline `flex-basis` style on the cell container.
   */
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Enables sorting on the column.
   */
  sortable: PropTypes.bool,
  /**
   * The type of sorting to apply to the column. This can be a string or a function.
   * If a string, it can be either 'string', 'stringFast', 'number', 'date', or a custom function
   * provided through the `sortTypes` prop on the `DataGrid`.
   * If a function is provided, it will receive the two data objects being compared,
   * plus the data key defined for that column. It should return a number with the same
   * semantics as the `Array.prototype.sort` compare function.
   */
  sortType: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * The text alignment for the column.
   */
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
  /**
   * The width of the column set as an inline `width` and `max-width` style on the cell container.
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

// This is used by the <DataGrid>, <AsyncDataGrid>, and <GridList> components
export const columnPropType = PropTypes.shape(columnPropTypeShape);
const filterShape = {
  /**
   * The row data key to filter on.
   */
  dataKey: PropTypes.string.isRequired,
  /**
   * The label for the filter.
   */
  label: PropTypes.string,
  /**
   * Options for the filter.
   */
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /**
   * The sorting order of the filter in the filter group.
   */
  order: PropTypes.number,
  /**
   * The type of filter to render.
   */
  type: PropTypes.oneOf(['select', 'checkbox', 'toggle']).isRequired
};
export const filterPropType = PropTypes.shape(filterShape);
const tableColumnPropTypeShape = {
  /**
   * Align text in the column to the left, right or center. Left is default.
   */
  align: PropTypes.oneOf(['left', 'center', 'right']),
  /**
   * Overrides the default cell renderer for the column. The cell must be a
   * function and it accepts three arguments, the cell data, a row id, and an
   * object that includes ALL row data (in case you need to access data from
   * another cell). Must return a React node to render.
   * (value, rowId, row) => node
   */
  cell: PropTypes.func,
  /**
   * The number of columns this column should span in the responsive layouts.
   * The key must match a `key` of a responsiveLayout object and the value
   * should be the number of columns.
   */
  colSpan: PropTypes.objectOf(PropTypes.number),
  /**
   * The data key for the column. This is used to get the cell value from the row data.
   */
  dataKey: PropTypes.string.isRequired,
  /**
   * Makes the column fixed (sticky) to the left side of the table.
   * The `width` prop is required when `fixed` is true.
   */
  fixed: PropTypes.bool,
  /**
   * The `groupId` is used for grouping column headers and must correspond to
   * a `groupId` within an object in the `columnGroupings` array.
   */
  groupId: PropTypes.string,
  /**
   * The displayed label for the column.
   */
  header: PropTypes.node,
  /**
   * Visually hides the column in the table but keeps it accessible for screen readers.
   */
  hidden: PropTypes.bool,
  /**
   *  Visually hides the column header but keeps it accessible for screen readers. Cannot be used on sortable or select columns.
   */
  hiddenHeader: PropTypes.bool,
  /**
   * Enables the column to be a row header.
   */
  rowHeader: PropTypes.bool,
  /**
   * Enables sorting on the column.
   */
  sortable: PropTypes.bool,
  /**
   * The type of sorting to apply to the column. This can be a string or a function.
   * If a string, it can be either 'string', 'stringFast', 'number', 'date', or a custom function
   * provided through the `sortTypes` prop on the `DataGrid`.
   * If a function is provided, it will receive the two data objects being compared,
   * plus the data key defined for that column. It should return a number with the same
   * semantics as the `Array.prototype.sort` compare function.
   */
  sortType: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * The text alignment for the column.
   */
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
  /**
   * The width of the column set as an inline `min-width` style on the `<th>` element.
   * Required when `fixed` is true.
   */
  width: PropTypes.string
};

// This is used by the <Table> component
export const tableColumnPropType = PropTypes.shape(tableColumnPropTypeShape);
const categoryColors = ['berry', 'blue', 'coral', 'dark-blue', 'fuchsia',
// deprecated
'gray-60', 'iris', 'noble-blue',
// FIXME: CSS class is "nobleBlue" and not "noble-blue"
'plum', 'salmon' // deprecated
];
const tableSubCategoriesPropTypeShape = {
  /**
   * The color for the subcategory row.
   */
  color: PropTypes.oneOf(categoryColors).isRequired,
  /**
   * The data key for the subcategory row, corresponding to the row data `subCategory` property.
   */
  key: PropTypes.string.isRequired,
  /**
   * The label for the subcategory that renders on the left side of the subcategory row.
   */
  label: PropTypes.string.isRequired
};
const tableCategoriesPropTypeShape = {
  /**
   * Overrides the default cell renderer for the category row. The function receives the category label and should return a React node.
   * When provided, the `value` prop is not rendered.
   */
  cell: PropTypes.func,
  /**
   * The bottom border color for the category row.
   */
  color: PropTypes.oneOf(categoryColors),
  /**
   * The data key for the category row, corresponding to the row data `category` property.
   */
  key: PropTypes.string.isRequired,
  /**
   * The label for the category that renders on the left side of the category row.
   */
  label: PropTypes.string.isRequired,
  /**
   * Defines subcategories for the category.
   */
  subCategories: PropTypes.arrayOf(PropTypes.shape(tableSubCategoriesPropTypeShape)),
  /**
   * The value for the category that renders on the right side of the category row.
   */
  value: PropTypes.string
};
export const tableCategoriesPropType = PropTypes.shape(tableCategoriesPropTypeShape);
const tableResponsiveLayoutShape = {
  /**
   * Apply class names to specific cells in this layout size. The key should be the column's dataKey.
   * The value should be a function which accepts the cell's value and returns a string of class names.
   * { [dataKey]: (value) => string }
   */
  cellClassNames: PropTypes.object,
  /**
   * The number of columns in this layout size
   */
  columns: PropTypes.number.isRequired,
  /**
   * Hide the table footer for this layout size
   */
  hideFooter: PropTypes.bool,
  /**
   * The name for this layout size. Used to map the `colSpan` properties in the column data.
   */
  key: PropTypes.string.isRequired,
  /**
   * The maximum window width for this layout size
   */
  maxWidth: PropTypes.number.isRequired,
  /**
   * Add a horizontal separator before specific columns in this layout size
   */
  separatorBeforeColumn: PropTypes.arrayOf(PropTypes.string)
};
export const tableResponsiveLayoutPropType = PropTypes.shape(tableResponsiveLayoutShape);